import {NgxUiLoaderComponent, NgxUiLoaderService} from 'ngx-ui-loader';

import {effect, inject, Injectable, Injector, Signal, signal, WritableSignal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {Loader} from './loader.types';

import GLOBAL_LOADER_ID = Loader.GLOBAL_LOADER_ID;
import DefaultConfig = Loader.DefaultConfig;
import NgxUiLoaderConfigExtended = Loader.NgxUiLoaderConfigExtended;

@Injectable({
	providedIn: 'root',
})
export class LoaderService {
	private readonly ngxUiLoaderService: NgxUiLoaderService = inject(NgxUiLoaderService);
	private readonly translate: TranslateService = inject(TranslateService);
	private readonly injector: Injector = inject(Injector);
	/* -------------------------------------------------- */
	private globalLoaderRef: NgxUiLoaderComponent = null;

	public setGlobalLoaderRef(globalLoaderRef: NgxUiLoaderComponent): void {
		this.globalLoaderRef = globalLoaderRef;
	}

	/* -------------------------------------------------- */
	private loaderConfig: WritableSignal<NgxUiLoaderConfigExtended> = signal(undefined);
	public loaderConfigurations: Signal<NgxUiLoaderConfigExtended> = this.loaderConfig.asReadonly();

	constructor() {
		this.resetLoaderConfig();
	}

	private setLoaderConfig(config: Partial<NgxUiLoaderConfigExtended>): void {
		if (config.text) {
			config.text = this.translate.instant(config.text);
		}
		this.loaderConfig.update((currentConfig: NgxUiLoaderConfigExtended) => {
			return {
				...currentConfig,
				...config,
			};
		});
	}

	private resetLoaderConfig(): void {
		this.setLoaderConfig({
			...this.ngxUiLoaderService.getDefaultConfig(),
			...DefaultConfig,
		});
	}

	public showLoader(config: Partial<Loader.NgxUiLoaderConfigExtended>): void {
		if (this.globalLoaderRef) {
			this.setLoaderConfig(config);
			this.ngxUiLoaderService.startLoader(config.loaderID || GLOBAL_LOADER_ID);
		}
	}

	public hideLoader(loaderID: string = GLOBAL_LOADER_ID): void {
		if (this.globalLoaderRef) {
			this.resetLoaderConfig();
			this.ngxUiLoaderService.stopLoader(loaderID);
		}
	}

	public showSignal(showSignal: Signal<boolean | string | Partial<Loader.NgxUiLoaderConfigExtended>>): void {
		effect(
			() => {
				const show = showSignal();
				if (!show) {
					this.hideLoader();
				} else if (typeof show === 'boolean') {
					show
						? this.showLoader({
								text: '',
							})
						: this.hideLoader();
				} else if (typeof show === 'string') {
					this.showLoader({text: show});
				} else if (typeof show === 'object') {
					this.showLoader(show);
				}
			},
			{
				injector: this.injector,
			},
		);
	}
}
